'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "error", "highRangeLabel", "label", "lowRangeLabel", "max", "maxPerRow", "maxWidth", "min", "name", "onChange", "required", "size", "style", "value"];
import I18n from 'I18n';
import PropTypes from 'prop-types';
import partial from 'react-utils/partial';
import Controllable from '../decorators/Controllable';
import SyntheticEvent from '../core/SyntheticEvent';
import UIIconButton from '../button/UIIconButton';
import UIFormLabel from '../form/UIFormLabel';
import UIButtonWrapper from '../layout/UIButtonWrapper';
import Small from '../elements/Small';
import UIFlex from '../layout/UIFlex';
import lazyEval from '../utils/lazyEval';
import createLazyPropType from '../utils/propTypes/createLazyPropType';
import { toShorthandSize } from '../utils/propTypes/tshirtSize';
import classNames from 'classnames';
import { BUTTON_XS_WIDTH, BUTTON_SM_WIDTH, BUTTON_LG_WIDTH } from 'HubStyleTokens/sizes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const GAP_BETWEEN_RATING_BUTTONS = 8;
const getButtonWidth = shorthandSize => {
  switch (shorthandSize) {
    case 'xs':
      return BUTTON_XS_WIDTH;
    case 'sm':
      return BUTTON_SM_WIDTH;
    default:
      return BUTTON_LG_WIDTH;
  }
};
const calculateWidth = (max, maxPerRow, min, shorthandSize) => {
  const buttonWidth = getButtonWidth(shorthandSize);
  return (maxPerRow || max - min + 1) * (parseInt(buttonWidth, 10) + GAP_BETWEEN_RATING_BUTTONS);
};
const renderRatingButtons = (min, max, name, onChange, size, value) => {
  const buttons = [];
  for (let i = min; i <= max; i++) {
    buttons.push( /*#__PURE__*/_jsx(UIIconButton, {
      active: i === value,
      name: name,
      onClick: partial(onChange, SyntheticEvent(i)),
      shape: "circle",
      size: size,
      children: i
    }, i));
  }
  return buttons;
};
const UINumberRating = props => {
  const {
      className,
      error,
      highRangeLabel,
      label,
      lowRangeLabel,
      max,
      maxPerRow,
      maxWidth,
      min,
      name,
      onChange,
      required,
      size,
      style,
      value
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const shorthandSize = toShorthandSize(size);
  const calculatedMaxWidth = maxWidth || calculateWidth(max, maxPerRow, min, shorthandSize);
  return /*#__PURE__*/_jsxs("div", Object.assign({}, rest, {
    className: classNames('private-form__rating', className),
    style: Object.assign({
      maxWidth: calculatedMaxWidth
    }, style),
    children: [label && /*#__PURE__*/_jsx(UIFormLabel, {
      error: error,
      required: required,
      className: "private-form__rating__label",
      children: label
    }), /*#__PURE__*/_jsx(UIButtonWrapper, {
      justify: "center",
      horizontalGap: GAP_BETWEEN_RATING_BUTTONS,
      verticalGap: GAP_BETWEEN_RATING_BUTTONS,
      children: renderRatingButtons(min, max, name, onChange, size, value)
    }), /*#__PURE__*/_jsxs(UIFlex, {
      justify: "between",
      direction: "row",
      align: "start",
      children: [/*#__PURE__*/_jsx("div", {
        className: "private-form__rating__range-label private-form__rating__range-label__low",
        children: /*#__PURE__*/_jsx(Small, {
          use: "help",
          children: lazyEval(lowRangeLabel)
        })
      }), /*#__PURE__*/_jsx("div", {
        className: "private-form__rating__range-label private-form__rating__range-label__high",
        children: /*#__PURE__*/_jsx(Small, {
          use: "help",
          children: lazyEval(highRangeLabel)
        })
      })]
    })]
  }));
};
UINumberRating.propTypes = {
  error: PropTypes.bool.isRequired,
  highRangeLabel: createLazyPropType(PropTypes.node).isRequired,
  label: PropTypes.node,
  lowRangeLabel: createLazyPropType(PropTypes.node).isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  maxPerRow: PropTypes.number,
  maxWidth: PropTypes.number,
  name: PropTypes.string.isRequired,
  size: UIIconButton.propTypes.size,
  onChange: PropTypes.func,
  required: PropTypes.bool.isRequired,
  value: PropTypes.number
};

/* eslint-disable-next-line hubspot-dev/no-default-props-assignment */
UINumberRating.defaultProps = {
  error: false,
  highRangeLabel: () => I18n.text('salesUI.UINumberRating.highRangeLabel'),
  lowRangeLabel: () => I18n.text('salesUI.UINumberRating.lowRangeLabel'),
  min: 1,
  max: 10,
  required: false,
  size: 'sm'
};
UINumberRating.displayName = 'UINumberRating';
export default Controllable(UINumberRating);